import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../../context/API';

export interface DeviceType {
  id: string;
  name: string;
  [key: string]: any;
}

interface DeviceTypesState {
  deviceTypes: DeviceType[];
  loading: boolean;
  error: string | null;
}

const initialState: DeviceTypesState = {
  deviceTypes: [],
  loading: false,
  error: null,
};

export const fetchDeviceTypes = createAsyncThunk<
  DeviceType[],
  void,
  { rejectValue: string }
>(
  'deviceTypes/fetchDeviceTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get('device-types', {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: false,
      });
      return response.data.data || [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch device types'
      );
    }
  }
);

export const createDeviceType = createAsyncThunk<
  DeviceType,
  { name: string },
  { rejectValue: string }
>(
  'deviceTypes/createDeviceType',
  async (deviceType, { rejectWithValue }) => {
    try {
      const response = await API.post('device-types', deviceType, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: false,
      });
      if (response.data.status === 200) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to create device type');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to create device type'
      );
    }
  }
);

export const updateDeviceType = createAsyncThunk<
  DeviceType,
  { id: string; name: string },
  { rejectValue: string }
>(
  'deviceTypes/updateDeviceType',
  async ({ id, name }, { rejectWithValue }) => {
    try {
      const response = await API.put(`device-types/${id}`, { name }, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: false,
      });
      if (response.data.status === 200) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to update device type');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to update device type'
      );
    }
  }
);

export const deleteDeviceType = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>(
  'deviceTypes/deleteDeviceType',
  async (id, { rejectWithValue }) => {
    try {
      const response = await API.delete(`device-types/${id}`, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: false,
      });
      if (response.data.status === 200) {
        return id;
      }
      return rejectWithValue(response.data.message || 'Failed to delete device type');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to delete device type'
      );
    }
  }
);

const deviceTypesSlice = createSlice({
  name: 'deviceTypes',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearDeviceTypes: (state) => {
      state.deviceTypes = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch device types
      .addCase(fetchDeviceTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeviceTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.deviceTypes = action.payload;
      })
      .addCase(fetchDeviceTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch device types';
      })
      // Create device type
      .addCase(createDeviceType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDeviceType.fulfilled, (state, action) => {
        state.loading = false;
        state.deviceTypes.push(action.payload);
      })
      .addCase(createDeviceType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create device type';
      })
      // Update device type
      .addCase(updateDeviceType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDeviceType.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.deviceTypes.findIndex((dt) => dt.id === action.payload.id);
        if (index !== -1) {
          state.deviceTypes[index] = action.payload;
        }
      })
      .addCase(updateDeviceType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update device type';
      })
      // Delete device type
      .addCase(deleteDeviceType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDeviceType.fulfilled, (state, action) => {
        state.loading = false;
        state.deviceTypes = state.deviceTypes.filter((dt) => dt.id !== action.payload);
      })
      .addCase(deleteDeviceType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete device type';
      });
  },
});

export const { clearError, clearDeviceTypes } = deviceTypesSlice.actions;
export default deviceTypesSlice.reducer;
